'use client';
import React, { useState, useEffect, useMemo } from 'react';
import type { ReactNode } from 'react';
import ProductActions from './_ProductActions';
import CompareSection, { Props as SectionProps } from './_Section';
import Section from '@repo/ui/src/components/Section';
import type { Props as SeoProps } from '@repo/ui/src/components/Seo';
import type { ImageProps } from '@repo/ui/src/types';
import { classNames } from '@repo/ui/src/utils/classNames';
import { useIsMD } from '@repo/ui/src/utils/useMediaQuery';
import { appConfig } from '@repo/ui/src/utils/appConfig';
import type { ComparisonProduct } from './types';

export interface ProductComparisonProps {
  sections?: Array<SectionProps>;
  products: Array<ComparisonProduct>;
  seo?: SeoProps;
  pageTitle: string;
  _testID: string;
  noSelectionImage: ImageProps;
  asContentRow?: boolean;
  vip?: boolean;
  renderTooltip?: (text: string) => ReactNode;
  onProductClick?: (product: ComparisonProduct, position: number) => void;
}

const ProductComparison = ({
  pageTitle,
  sections,
  products,
  _testID,
  noSelectionImage,
  asContentRow,
  vip,
  renderTooltip,
  onProductClick,
}: ProductComparisonProps) => {
  const mdAndUp = useIsMD();
  const allSkus = useMemo(() => products.map((p) => p.sku), [products]);
  const [selectedSkus, setSelectedSkus] = useState<Array<string>>(() => allSkus.slice(0, 2));

  useEffect(() => {
    let skus = !mdAndUp ? allSkus.slice(0, 2) : [...allSkus.slice(0, 2), ''];
    if (asContentRow) {
      skus = !mdAndUp ? allSkus.slice(0, 2) : allSkus.slice(0, 4);
    }
    setSelectedSkus(skus);
  }, [mdAndUp, allSkus]);

  const HeadingTag = asContentRow ? 'h2' : 'h1';

  return (
    <Section texture>
      <div data-test-id={`hero-wrapper_${_testID}`} className="py-10 md:py-20 text-center">
        <HeadingTag
          className={classNames(
            appConfig.isTraeger ? 'text-neutral-500' : 'text-neutral-50',
            asContentRow ? 'tra-text-web-heading-2' : 'tra-text-web-heading-1',
          )}
        >
          {pageTitle}
        </HeadingTag>
      </div>

      <div className="bg-white md:mx-10 md:py-20 md:px-10">
        <div className="w-full">
          {!!products?.length && (
            <div>
              <ProductActions
                selectedSkus={selectedSkus}
                selectableSkus={allSkus}
                products={products}
                noSelectionImage={noSelectionImage}
                updateSelectedProducts={(sku, index) => {
                  setSelectedSkus(selectedSkus.map((oldSku, i) => (index === i ? sku : oldSku)));
                }}
                _testID={_testID}
                asContentRow={asContentRow}
                vip={vip}
                onProductClick={onProductClick}
              />

              {sections?.map((section, idx) => (
                <CompareSection
                  {...section}
                  key={idx}
                  selectedSkus={selectedSkus}
                  products={products}
                  asContentRow={asContentRow}
                  renderTooltip={renderTooltip}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </Section>
  );
};

export default ProductComparison;
